import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import '../UniversalApiPage.css';
import '../IntegrationRouteCodePage.css';     // .irc-rows, .irc-seq
import '../PaymentPorterPage.css';           // .pp-rules-add-btn

// THE UNIVERSAL API CUSTOM CODE ATTACHED TO ONE TARGET, in run order — list, reorder, detach, attach one
// already written for the same system, or start a new one.
//
// ONE COMPONENT, TWO TARGETS. A snippet (V528) is attached either to a property's backend binding
// (Universal API > Authentication > <hotel> > <backend>) or to a pipeline template's route (V533, opened
// from the Integration column's Custom Code button on the designer). The operations are identical and the
// server answers both the same way, so the screens share this rather than each keeping a copy.
//
// target: { kind: 'access' | 'route', id }
// onOpen(codeId) / onNew() — where a snippet is edited; the caller decides, because it knows the way back.
// onLoaded({ target, attached, available }) — optional; the route page reads its template id from it.

const API = {
    access: {
        list: legacyUapiService.listAccessCustomCode,
        attach: legacyUapiService.attachAccessCustomCode,
        setSequence: legacyUapiService.setAccessCustomCodeSequence,
        detach: legacyUapiService.detachAccessCustomCode,
    },
    route: {
        list: legacyUapiService.listRouteCustomCode,
        attach: legacyUapiService.attachRouteCustomCode,
        setSequence: legacyUapiService.setRouteCustomCodeSequence,
        detach: legacyUapiService.detachRouteCustomCode,
    },
};

const errText = (e, fallback) =>
    e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

const UapiCustomCodeAttachments = ({
    target,
    systemName,
    title = 'Custom code',
    intro,          // what this list runs on, said in the caller's terms
    emptyText,
    detachTitle = (c) => `Stop running ${c.name} here — the code itself is kept`,
    onOpen,
    onNew,
    onLoaded,
    onError,
    disabled = false,
}) => {
    const api = API[target.kind];
    const [customCode, setCustomCode] = useState({ attached: [], available: [] });
    const [busy, setBusy] = useState(false);
    const [attachPick, setAttachPick] = useState('');

    // Every change answers with the target's whole list, so the response IS the new state.
    const apply = useCallback((res) => {
        const d = (res && res.data) || res || {};
        setCustomCode({ attached: d.attached || [], available: d.available || [] });
        if (onLoaded) onLoaded(d);
    }, [onLoaded]);

    useEffect(() => {
        let cancelled = false;
        api.list(target.id)
            .then(res => { if (!cancelled) apply(res); })
            .catch(e => { if (!cancelled && onError) onError(errText(e, 'Could not load the custom code')); });
        return () => { cancelled = true; };
    }, [target.kind, target.id]);

    const act = async (fn, fallback) => {
        setBusy(true);
        if (onError) onError(null);
        try {
            apply(await fn());
        } catch (e) {
            if (onError) onError(errText(e, fallback));
        } finally {
            setBusy(false);
        }
    };

    const attachCode = () => {
        if (!attachPick) return;
        act(() => api.attach(target.id, attachPick), 'Could not attach that custom code');
        setAttachPick('');
    };

    const detachCode = (c) => act(() => api.detach(target.id, c.id), 'Could not detach that custom code');

    // Swap with the neighbour, then renumber the whole list in tens — the two rows' old numbers may be
    // equal (attached in one go), and swapping equal numbers changes nothing.
    const moveCode = (index, delta) => act(async () => {
        const order = [...customCode.attached];
        const [row] = order.splice(index, 1);
        order.splice(index + delta, 0, row);
        let last;
        for (let i = 0; i < order.length; i++) {
            if (Number(order[i].sequence) !== (i + 1) * 10) {
                last = await api.setSequence(target.id, order[i].id, (i + 1) * 10);
            }
        }
        return last || api.list(target.id);
    }, 'Could not reorder the custom code');

    const locked = busy || disabled;

    return (
        <div className="uapi-modules uapi-modules-standalone">
            <div className="uapi-section-bar">
                <h2 className="uapi-section-title">{title}</h2>
                {onNew && (
                    <button type="button" className="pp-rules-add-btn" onClick={onNew} disabled={disabled}>
                        <Plus size={12} /> New custom code
                    </button>
                )}
            </div>
            {intro && <p className="lu-flow-note" style={{ marginTop: 0 }}>{intro}</p>}

            {customCode.attached.length === 0 ? (
                <p className="lu-muted" style={{ margin: '0 0 0.5rem' }}>{emptyText}</p>
            ) : (
                <ul className="irc-rows uapi-cc-rows">
                    {customCode.attached.map((c, i) => (
                        <li key={c.id} className="irc-row irc-row-static">
                            <span className="irc-seq">{i + 1}</span>
                            <button type="button" className="uapi-cc-open" onClick={() => onOpen(c.id)}>
                                {c.name}
                            </button>
                            <span className={`uapi-cc-state ${c.active === 'Y' ? 'is-on' : ''}`}>
                                {c.active === 'Y' ? 'Active' : 'Inactive — not run'}
                            </span>
                            {/* The modules it runs on — it only touches those calls. */}
                            {(c.modules || []).length ? c.modules.map(p => (
                                <span key={p} className="uapi-module-chip uapi-module-chip-sm">{p}</span>
                            )) : <span className="uapi-cc-hook is-error">no module — runs nowhere</span>}
                            {c.compileError ? (
                                <span className="uapi-cc-hook is-error" title={c.compileError}>does not compile</span>
                            ) : (
                                <>
                                    {c.hasBefore && <span className="uapi-cc-hook">before</span>}
                                    {c.hasAfter && <span className="uapi-cc-hook">after</span>}
                                </>
                            )}
                            <span className="uapi-cc-row-actions">
                                <button type="button" className="uapi-module-chip-remove" title="Run earlier"
                                    disabled={locked || i === 0} onClick={() => moveCode(i, -1)}>
                                    <ArrowUp size={12} />
                                </button>
                                <button type="button" className="uapi-module-chip-remove" title="Run later"
                                    disabled={locked || i === customCode.attached.length - 1} onClick={() => moveCode(i, 1)}>
                                    <ArrowDown size={12} />
                                </button>
                                <button type="button" className="uapi-module-chip-remove"
                                    title={detachTitle(c)}
                                    disabled={locked} onClick={() => detachCode(c)}>
                                    <X size={12} />
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            {/* Written once for the system, attached wherever it is wanted — never copied. */}
            {customCode.available.length > 0 && (
                <div className="uapi-module-add">
                    <select value={attachPick} onChange={(e) => setAttachPick(e.target.value)} disabled={locked}>
                        <option value="">Attach existing {systemName} custom code…</option>
                        {customCode.available.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.name}{c.active === 'Y' ? '' : ' (inactive)'}
                                {Number(c.attached_count) ? ` — on ${c.attached_count} backend${Number(c.attached_count) === 1 ? '' : 's'}` : ''}
                            </option>
                        ))}
                    </select>
                    <button type="button" className="pp-rules-add-btn" onClick={attachCode} disabled={!attachPick || locked}>
                        Attach
                    </button>
                </div>
            )}
        </div>
    );
};

export default UapiCustomCodeAttachments;
